import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveComWixpressPromoteSeoRobotsServerV2LlmsServiceV2Url(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'www._base_domain_': [
      {
        srcPath: '/_api/promote-seo-robots-server',
        destPath: '/api',
      },
      {
        srcPath: '/_api/promote-seo-robots-server/v2/robots',
        destPath: '/v2/robots',
      },
      {
        srcPath: '/_api/promote-seo-robots-server/v2/ads',
        destPath: '/v2/ads',
      },
      {
        srcPath: '/_api/promote-seo-robots-server/v2/llms',
        destPath: '/v2/llms',
      },
    ],
    'api._api_base_domain_': [
      {
        srcPath: '/promote-seo-robots-server',
        destPath: '',
      },
    ],
    'manage._base_domain_': [
      {
        srcPath: '/_api/promote-seo-robots-server',
        destPath: '/api',
      },
      {
        srcPath: '/_api/promote-seo-robots-server/v2/robots',
        destPath: '/v2/robots',
      },
      {
        srcPath: '/_api/promote-seo-robots-server/v2/ads',
        destPath: '/v2/ads',
      },
      {
        srcPath: '/_api/promote-seo-robots-server/v2/llms',
        destPath: '/v2/llms',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/promote-seo-robots-server/v2/robots',
        destPath: '/v2/robots',
      },
      {
        srcPath: '/promote-seo-robots-server/v2/ads',
        destPath: '/v2/ads',
      },
      {
        srcPath: '/promote-seo-txt-file-server/v2/ads',
        destPath: '/v2/ads',
      },
      {
        srcPath: '/promote-seo-txt-file-server/v2/robots',
        destPath: '/v2/robots',
      },
      {
        srcPath: '/promote-seo-robots-server/v2/llms',
        destPath: '/v2/llms',
      },
      {
        srcPath: '/promote-seo-txt-file-server/v2/llms',
        destPath: '/v2/llms',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/promote/marketing/v2/ads',
        destPath: '/v2/ads',
      },
      {
        srcPath: '/promote/seo/v2/llms',
        destPath: '/v2/llms',
      },
      {
        srcPath: '/promote/seo/v2/robots',
        destPath: '/v2/robots',
      },
    ],
    _: [
      {
        srcPath: '/promote/marketing/v2/ads',
        destPath: '/v2/ads',
      },
      {
        srcPath: '/promote/seo/v2/llms',
        destPath: '/v2/llms',
      },
      {
        srcPath: '/promote/seo/v2/robots',
        destPath: '/v2/robots',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_seo_llms-txt';

/** Retrieves the `llms.txt` file entity. */
export function getLlmsTxt(payload: object): RequestOptionsFactory<any> {
  function __getLlmsTxt({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.promote.seo.v2.llms_txt',
      method: 'GET' as any,
      methodFqn:
        'com.wixpress.promote.seo.robots.server.v2.LlmsServiceV2.GetLlmsTxt',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressPromoteSeoRobotsServerV2LlmsServiceV2Url({
        protoPath: '/v2/llms',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __getLlmsTxt;
}

/**
 * Updates the `llms.txt` file entity.
 *
 * The content can be set to an empty string to create a blank file.
 *
 * To restore Wix's default content, set the `default` field to `true` and omit the `content` field.
 */
export function updateLlmsTxt(payload: object): RequestOptionsFactory<any> {
  function __updateLlmsTxt({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.promote.seo.v2.llms_txt',
      method: 'PUT' as any,
      methodFqn:
        'com.wixpress.promote.seo.robots.server.v2.LlmsServiceV2.UpdateLlmsTxt',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressPromoteSeoRobotsServerV2LlmsServiceV2Url({
        protoPath: '/v2/llms',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __updateLlmsTxt;
}

/**
 * Appends additional content to the existing `llms.txt` file.
 *
 * The provided content will be added to the end of the current file content.
 *
 * To reset to Wix's default content instead, set `default` to `true` and omit the `content` field.
 */
export function appendLlmsTxt(payload: object): RequestOptionsFactory<any> {
  function __appendLlmsTxt({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.promote.seo.v2.llms_txt',
      method: 'PATCH' as any,
      methodFqn:
        'com.wixpress.promote.seo.robots.server.v2.LlmsServiceV2.AppendLlmsTxt',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressPromoteSeoRobotsServerV2LlmsServiceV2Url({
        protoPath: '/v2/llms',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __appendLlmsTxt;
}

/**
 * Updates only the hidden state of the Llms.txt file.
 * Content remains unchanged - only visibility is modified.
 */
export function updateLlmsTxtHiddenStatus(
  payload: object
): RequestOptionsFactory<any> {
  function __updateLlmsTxtHiddenStatus({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.promote.seo.v2.llms_txt',
      method: 'PATCH' as any,
      methodFqn:
        'com.wixpress.promote.seo.robots.server.v2.LlmsServiceV2.UpdateLlmsTxtHiddenStatus',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressPromoteSeoRobotsServerV2LlmsServiceV2Url({
        protoPath: '/v2/llms/hidden',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __updateLlmsTxtHiddenStatus;
}
